import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { MessageSquareHeart, Star, Search, Ban, Eye, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  Sent: 'bg-blue-100 text-blue-700',
  Opened: 'bg-amber-100 text-amber-700',
  Submitted: 'bg-green-100 text-green-700',
  Closed: 'bg-slate-200 text-slate-600',
  Pending: 'bg-slate-100 text-slate-600',
};

const Kpi = ({ label, value, testid }) => (
  <Card><CardContent className="p-4">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-2xl font-bold" data-testid={testid}>{value}</p>
  </CardContent></Card>
);

export default function ClientVisitFeedbackPage() {
  const { getAuthHeader, employee } = useAuth();
  const allowed = ['Management', 'Super Admin'].includes(employee?.role);
  const [rows, setRows] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', ticket_id: '', company: '' });
  const [detail, setDetail] = useState(null);
  const [closeRec, setCloseRec] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.status !== 'all') p.append('status', filters.status);
      if (filters.ticket_id.trim()) p.append('ticket_id', filters.ticket_id.trim());
      if (filters.company.trim()) p.append('company', filters.company.trim());
      const [tr, an] = await Promise.all([
        fetch(`${API}/cfv-feedback/tracking?${p.toString()}`, { headers: getAuthHeader() }),
        fetch(`${API}/cfv-feedback/analytics`, { headers: getAuthHeader() }),
      ]);
      if (tr.ok) { const d = await tr.json(); setRows(d.records || []); }
      if (an.ok) setAnalytics(await an.json());
    } catch (e) { toast.error('Failed to load feedback data'); }
    finally { setLoading(false); }
  }, [getAuthHeader, filters]);

  useEffect(() => { if (allowed) fetchData(); }, [allowed, fetchData]);

  const openDetail = async (id) => {
    const r = await fetch(`${API}/cfv-feedback/${id}`, { headers: getAuthHeader() });
    if (r.ok) setDetail(await r.json());
  };

  const doClose = async () => {
    const r = await fetch(`${API}/cfv-feedback/${closeRec.id}/close`, { method: 'POST', headers: getAuthHeader() });
    if (r.ok) { toast.success('Feedback link closed'); setCloseRec(null); fetchData(); }
    else { toast.error('Failed to close'); }
  };

  if (!allowed) return <Layout><div className="p-8 text-center text-muted-foreground" data-testid="cvf-denied">Only Management and Super Admin can view Client Visit Feedback.</div></Layout>;

  const fmt = (s) => s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-visit-feedback-page">
        <div className="flex items-center gap-3">
          <MessageSquareHeart className="h-7 w-7 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold">Client Visit Feedback</h1>
            <p className="text-sm text-muted-foreground">Post-visit feedback tracking & analytics</p>
          </div>
        </div>

        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <Kpi label="Total Visits" value={analytics.total_visits} testid="cvf-total-visits" />
            <Kpi label="Total Visitors" value={analytics.total_visitors} testid="cvf-total-visitors" />
            <Kpi label="Submitted" value={analytics.submitted} testid="cvf-submitted" />
            <Kpi label="Pending" value={analytics.pending} testid="cvf-pending" />
            <Kpi label="Closed" value={analytics.closed} testid="cvf-closed" />
            <Kpi label="Response Rate" value={`${analytics.response_rate}%`} testid="cvf-response-rate" />
            <Kpi label="Avg Rating" value={analytics.avg_rating ?? '—'} testid="cvf-avg-rating" />
          </div>
        )}

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-muted-foreground">Status</label>
                <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="cvf-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Sent">Sent</SelectItem>
                    <SelectItem value="Opened">Opened</SelectItem>
                    <SelectItem value="Submitted">Submitted</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs text-muted-foreground">Ticket</label>
                <Input placeholder="REQ-..." value={filters.ticket_id} onChange={(e) => setFilters(f => ({ ...f, ticket_id: e.target.value }))} data-testid="cvf-filter-ticket" />
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs text-muted-foreground">Company</label>
                <Input placeholder="Company" value={filters.company} onChange={(e) => setFilters(f => ({ ...f, company: e.target.value }))} data-testid="cvf-filter-company" />
              </div>
              <Button variant="outline" onClick={fetchData} data-testid="cvf-apply-filters"><Search className="h-4 w-4 mr-1" />Apply</Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
            ) : rows.length === 0 ? (
              <p className="py-10 text-center text-muted-foreground" data-testid="cvf-empty">No feedback records yet. Feedback is sent automatically the day after a visit's final departure, or manually from a Client Factory Visit ticket.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="cvf-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Visitor</TableHead><TableHead>Company</TableHead><TableHead>Ticket</TableHead>
                      <TableHead>Status</TableHead><TableHead>Rating</TableHead><TableHead>Sent</TableHead>
                      <TableHead>Submitted</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.id} data-testid={`cvf-row-${r.id}`}>
                        <TableCell className="font-medium">{r.visitor_name}<div className="text-xs text-muted-foreground">{r.visitor_email}</div></TableCell>
                        <TableCell>{r.visitor_company || '—'}</TableCell>
                        <TableCell>{r.ticket_id}</TableCell>
                        <TableCell><Badge className={STATUS_COLORS[r.status] || ''}>{r.status}</Badge>{r.email_status === 'suppressed_preview' && <div className="text-[10px] text-amber-600 mt-1">email suppressed (preview)</div>}</TableCell>
                        <TableCell>{r.avg_rating != null ? <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{r.avg_rating}</span> : '—'}</TableCell>
                        <TableCell className="text-xs">{fmt(r.sent_at)}</TableCell>
                        <TableCell className="text-xs">{fmt(r.submitted_at)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openDetail(r.id)} data-testid={`cvf-view-${r.id}`}><Eye className="h-4 w-4" /></Button>
                          {r.status !== 'Closed' && r.status !== 'Submitted' && (
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setCloseRec(r)} data-testid={`cvf-close-${r.id}`}><Ban className="h-4 w-4 text-red-600" /></Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cvf-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.visitor_name} — Feedback</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">Company:</span> {detail.visitor_company || '—'}</div>
                <div><span className="text-muted-foreground">Ticket:</span> {detail.ticket_id}</div>
                <div><span className="text-muted-foreground">Status:</span> {detail.status}</div>
                <div><span className="text-muted-foreground">Avg Rating:</span> {detail.avg_rating ?? '—'}</div>
              </div>
              {detail.status === 'Submitted' && detail.answers ? (
                <div className="space-y-3 border-t pt-3">
                  {(detail.survey_questions || []).filter(q => q.type !== 'section').map(q => (
                    <div key={q.id}>
                      <p className="font-medium">{q.label}</p>
                      <p className="text-muted-foreground">{Array.isArray(detail.answers[q.id]) ? detail.answers[q.id].join(', ') : (detail.answers[q.id] ?? '—').toString()}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-muted-foreground border-t pt-3">No response submitted yet.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!closeRec} onOpenChange={(o) => !o && setCloseRec(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this feedback link?</AlertDialogTitle>
            <AlertDialogDescription>{closeRec?.visitor_name} will no longer be able to submit feedback. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doClose} data-testid="cvf-confirm-close">Close Link</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
